import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
Button,
TextField,
Select,
MenuItem,
FormControl,
InputLabel,
Modal,
Box,
Typography,
Alert,
List,
ListItem,
ListItemText,
Badge,
Paper,
Divider,
useMediaQuery,
useTheme,
InputAdornment,
Chip,
Stepper,
Step,
StepLabel,
Fade,
Zoom,
Container,
Card,
CardContent,
CircularProgress,
Slide,
LinearProgress,
Autocomplete,
FormHelperText,
IconButton,
Dialog,
DialogTitle,
DialogContent,
DialogActions
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
Person,
Email,
Phone,
School,
Flag,
Lock,
Visibility,
VisibilityOff,
CheckCircle,
Cancel,
Search,
Groups,
AppRegistration,
Login,
ArrowBack,
DoneAll,
Send,
Add,
Close
} from '@mui/icons-material';
import {APIResearcherStore, APIUniversitiesList, APIUniversityStore} from '../utilities/APIS';
import { headers } from '../utilities/Env';
import useCustomToast from '../hooks/useToast';
import { TEXT_CONNECTION_ERROR} from '../utilities/Text';
import logoIcone from "../utilities/imgs/logo.png";
import {countryCodes} from "../utilities/Constants";

// Validation constants
const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const CONTACT_REGEX = /^\d{7,15}$/; // 7-15 digits for phone number after country code

// Styled Components
const ModernCard = styled(Card)(({ theme }) => ({
background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
backdropFilter: 'blur(10px)',
border: `1px solid ${theme.palette.divider}`,
borderRadius: theme.spacing(3),
boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
}));

const StyledModal = styled(Modal)(({ theme }) => ({
display: 'flex',
alignItems: 'center',
justifyContent: 'center',
padding: theme.spacing(2),
}));

const ModalContent = styled(Box)(({ theme }) => ({
backgroundColor: theme.palette.background.paper,
padding: theme.spacing(4),
borderRadius: theme.spacing(2),
width: '90%',
maxWidth: '600px',
maxHeight: '90vh',
overflow: 'auto',
background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
[theme.breakpoints.down('sm')]: {
    width: '95%',
    padding: theme.spacing(3),
},
}));

const UniversityList = styled(List)(({ theme }) => ({
maxHeight: '300px',
overflow: 'auto',
border: `1px solid ${theme.palette.divider}`,
borderRadius: theme.spacing(1),
'& .MuiListItem-root': {
    borderRadius: theme.spacing(1),
    margin: theme.spacing(0.5),
    '&:hover': {
    backgroundColor: theme.palette.action.hover,
    },
    '&.Mui-selected': {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.contrastText,
    },
},
}));

const FeatureChip = styled(Chip)(({ theme }) => ({
margin: theme.spacing(0.5),
color: theme.palette.success.main,
}));

const PasswordStrengthBar = styled(Box)(({ theme, strength }) => ({
height: 4,
borderRadius: 2,
marginTop: theme.spacing(1),
backgroundColor: 
    strength === 'weak' ? theme.palette.error.main :
    strength === 'medium' ? theme.palette.warning.main :
    theme.palette.success.main,
transition: 'all 0.3s ease',
}));

const LogoImage = styled('img')(({ theme }) => ({
height: 80,
width: 80,
borderRadius: 12,
objectFit: 'contain',
filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.1))',
}));

const ActionButton = styled(Button)(({ theme }) => ({
    textTransform: 'none',
    fontWeight: 'bold',
    py: 1.2,
    borderRadius: 2,
    transition: 'all 0.3s ease',
    fontSize: '0.95rem',
    '&:hover': {
        transform: 'translateY(-2px)',
    }
}));

const Register = () => {
const navigate = useNavigate();
const [postStatus, setPostSatus] = useState('ADD');
const showToast = useCustomToast();
const [error, setError] = useState(null);
const theme = useTheme();
const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

// Form state
const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    countryCode: '+255', // Default to Tanzania (East Africa)
    contact: '',
    title: '',
    nationality: '',
    affiliationId: '', // Changed from universityId
    affiliationName: '', // Changed from universityName
    typedAffiliation: '', // Changed from typedUniversity
    password: '',
    passwordRetype: ''
});

// UI state
const [affiliations, setAffiliations] = useState([]); // Changed from universities
const [affiliationSearchInput, setAffiliationSearchInput] = useState(''); // Changed from universitySearchInput
const [showWhyModal, setShowWhyModal] = useState(false);
const [showAffiliationModal, setShowAffiliationModal] = useState(false); // Changed from showUniversityModal
const [showAddAffiliationDialog, setShowAddAffiliationDialog] = useState(false); // Changed from showAddUniversityDialog
const [newAffiliationName, setNewAffiliationName] = useState(''); // Changed from newUniversityName
const [newAffiliationCreator, setNewAffiliationCreator] = useState(''); // New state for creator
const [isAddingAffiliation, setIsAddingAffiliation] = useState(false); // Changed from isAddingUniversity
const [showPassword, setShowPassword] = useState(false);
const [activeStep, setActiveStep] = useState(0);
const [showAffiliationNotListed, setShowAffiliationNotListed] = useState(false); // Changed from showUniversityNotListed
const [passwordStrength, setPasswordStrength] = useState('');
const [isSubmitting, setIsSubmitting] = useState(false);
const [showCountryCodeSelect, setShowCountryCodeSelect] = useState(false);

// Validation state
const [errors, setErrors] = useState({
    fullName: '',
    email: '',
    contact: '',
    title: '',
    nationality: '',
    affiliationId: '', // Changed from universityId
    password: '',
    passwordMatch: '',
    form: '',
    userExists: ''
});

// Step validation state
const [stepValidations, setStepValidations] = useState({
    0: false, // Personal Info
    1: false, // Academic Details
    2: false  // Security
});

// Loading and error state
const [loading, setLoading] = useState(false);

// Load initial data
useEffect(() => {
    fetchData();
}, []);

// Password strength calculator
useEffect(() => {
    if (formData.password) {
    const strength = calculatePasswordStrength(formData.password);
    setPasswordStrength(strength);
    } else {
    setPasswordStrength('');
    }
}, [formData.password]);

// Validate current step when form data changes
useEffect(() => {
    validateCurrentStep();
}, [formData, activeStep]);

const calculatePasswordStrength = (password) => {
    let score = 0;
    if (password.length >= 8) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[@$!%*?&]/.test(password)) score++;
    
    if (score <= 2) return 'weak';
    if (score <= 4) return 'medium';
    return 'strong';
};

const validateCurrentStep = () => {
    let isValid = true;
    const newStepValidations = { ...stepValidations };
    const newErrors = { ...errors };

    switch (activeStep) {
        case 0: // Personal Info
            newErrors.fullName = '';
            newErrors.contact = '';
            newErrors.title = '';
            newErrors.nationality = '';

            if (!formData.fullName.trim()) {
                newErrors.fullName = 'Full name is required';
                isValid = false;
            }

            if (!formData.contact.trim()) {
                newErrors.contact = 'Contact number is required';
                isValid = false;
            } else if (!CONTACT_REGEX.test(formData.contact)) {
                newErrors.contact = `Contact number must be ${formData.countryCode === '+1' ? '10' : '7-15'} digits`;
                isValid = false;
            }

            if (!formData.title.trim()) {
                newErrors.title = 'Title is required';
                isValid = false;
            }

            if (!formData.nationality.trim()) {
                newErrors.nationality = 'Nationality is required';
                isValid = false;
            }
            break;
        
        case 1: // Academic Details
            newErrors.affiliationId = ''; // Changed from universityId
            newErrors.email = '';

            if (!formData.affiliationId) { // Changed from universityId
                newErrors.affiliationId = 'Affiliation selection is required'; // Changed text
                isValid = false;
            }

            if (!formData.email.trim()) {
                newErrors.email = 'Email is required';
                isValid = false;
            } else if (!EMAIL_REGEX.test(formData.email)) {
                newErrors.email = 'Please enter a valid email address';
                isValid = false;
            }
            break;
        
        case 2: // Security
            newErrors.password = '';
            newErrors.passwordMatch = '';

            if (!formData.password) {
                newErrors.password = 'Password is required';
                isValid = false;
            } else if (!PASSWORD_REGEX.test(formData.password)) {
                newErrors.password = 'Password must contain at least 8 characters, one uppercase, one lowercase, one number and one special character';
                isValid = false;
            }

            if (!formData.passwordRetype) {
                newErrors.passwordMatch = 'Please confirm your password';
                isValid = false;
            } else if (formData.password !== formData.passwordRetype) {
                newErrors.passwordMatch = 'Passwords do not match';
                isValid = false;
            }
            break;
        
        default:
            isValid = false;
    }

    newStepValidations[activeStep] = isValid;
    setStepValidations(newStepValidations);
    setErrors(newErrors);
    return isValid;
};

const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
    const [affiliationsRes] = await Promise.all([ // Changed variable name
        axios.get(APIUniversitiesList, { headers }),
    ]);

    const data = Array.isArray(affiliationsRes.data.data) ? affiliationsRes.data.data.reverse() : [];
    setAffiliations(data); // Changed from setUniversities
    showToast("Success", `Loaded ${data.length} affiliations`, "success"); // Changed text
    } catch (error) {
    let errorMessage = "Failed to load data";
    if (error.response) {
        errorMessage = error.response.data.message || error.message;
    } else if (error.code === "ERR_NETWORK") {
        errorMessage = TEXT_CONNECTION_ERROR;
    }
    setError(errorMessage);
    showToast("Error", errorMessage, "error");
    } finally {
    setLoading(false);
    }
};

const handleAddNewAffiliation = async () => { // Renamed function
    // Validate both fields are required
    if (!newAffiliationName.trim()) {
        showToast("Validation Error", "Please enter an affiliation name", "warning");
        return;
    }
    
    if (!newAffiliationCreator.trim()) {
        showToast("Validation Error", "Please enter the creator name", "warning");
        return;
    }

    setIsAddingAffiliation(true);
    
    try {
        const formDataToSend = new FormData();
        formDataToSend.append('name', newAffiliationName.trim());
        formDataToSend.append('creator', newAffiliationCreator.trim());

        const response = await axios.post(APIUniversityStore, formDataToSend, { headers });
        
        if (response.data.status === "success") {
            // Refresh affiliations list
            await fetchData();
            
            // Find the newly created affiliation in the updated list
            const newAffiliation = affiliations.find(a => a.name.toLowerCase() === newAffiliationName.trim().toLowerCase());
            
            if (newAffiliation) {
                // Auto-select the newly created affiliation
                setFormData(prev => ({
                    ...prev,
                    affiliationId: newAffiliation.id,
                    affiliationName: newAffiliation.name
                }));
                showToast("Success", `Affiliation "${newAffiliationName}" added and selected!`, "success");
            } else {
                // If not found in list, wait a moment and try to find it
                setTimeout(() => {
                    const found = affiliations.find(a => a.name.toLowerCase() === newAffiliationName.trim().toLowerCase());
                    if (found) {
                        setFormData(prev => ({
                            ...prev,
                            affiliationId: found.id,
                            affiliationName: found.name
                        }));
                    }
                }, 1000);
                showToast("Success", `Affiliation "${newAffiliationName}" added! Please select it from the list.`, "success");
            }
            
            // Close dialog and reset
            setShowAddAffiliationDialog(false);
            setNewAffiliationName('');
            setNewAffiliationCreator('');
            setErrors(prev => ({ ...prev, affiliationId: '' }));
        } else {
            showToast("Error", response.data.message || "Failed to add affiliation", "error");
        }
    } catch (error) {
        console.error("Error adding affiliation:", error);
        if (error.response) {
            if (error.response.status === 422) {
                const errors = error.response.data.errors;
                let errorMessages = [];
                for (const field in errors) {
                    errorMessages.push(...errors[field]);
                }
                showToast("Validation Error", errorMessages.join("\n"), "error");
            } else {
                showToast("Error", error.response.data.message || "An error occurred while adding affiliation", "error");
            }
        } else if (error.code === "ERR_NETWORK") {
            showToast("Connection Error", TEXT_CONNECTION_ERROR, "error");
        } else {
            showToast("Error", "An unexpected error occurred", "error");
        }
    } finally {
        setIsAddingAffiliation(false);
    }
};

const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'contact') {
        let numericValue = value.replace(/\D/g, '');
        let maxLength = 15;
        if (formData.countryCode === '+1') maxLength = 10;
        numericValue = numericValue.slice(0, maxLength);
        
        setFormData(prev => ({
            ...prev,
            [name]: numericValue
        }));
    } else {
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    }

    if (errors[name] || errors.form) {
        setErrors(prev => ({ ...prev, [name]: '', form: '' }));
    }
};

const handleCountryCodeChange = (code) => {
    setFormData(prev => ({
        ...prev,
        countryCode: code,
        contact: ''
    }));
    setShowCountryCodeSelect(false);
};

const handleAffiliationSelect = (event, selectedAffiliation) => { // Renamed function
    if (selectedAffiliation) {
        setFormData(prev => ({
            ...prev,
            affiliationId: selectedAffiliation.id,
            affiliationName: selectedAffiliation.name
        }));
        setErrors(prev => ({ ...prev, affiliationId: '' }));
        showToast("Success", `Selected: ${selectedAffiliation.name}`, "success");
    } else {
        setFormData(prev => ({
            ...prev,
            affiliationId: '',
            affiliationName: ''
        }));
    }
};

const validateForm = () => {
    let valid = true;
    const newErrors = {
        fullName: '',
        email: '',
        contact: '',
        title: '',
        nationality: '',
        affiliationId: '',
        password: '',
        passwordMatch: '',
        form: '',
        userExists: ''
    };

    if (!formData.fullName.trim()) {
        newErrors.fullName = 'Full name is required';
        valid = false;
    }

    if (!formData.contact.trim()) {
        newErrors.contact = 'Contact number is required';
        valid = false;
    } else if (!CONTACT_REGEX.test(formData.contact)) {
        newErrors.contact = `Contact number must be ${formData.countryCode === '+1' ? '10' : '7-15'} digits`;
        valid = false;
    }

    if (!formData.title.trim()) {
        newErrors.title = 'Title is required';
        valid = false;
    }

    if (!formData.nationality.trim()) {
        newErrors.nationality = 'Nationality is required';
        valid = false;
    }

    if (!formData.affiliationId) {
        newErrors.affiliationId = 'Affiliation selection is required';
        valid = false;
    }

    if (!formData.email.trim()) {
        newErrors.email = 'Email is required';
        valid = false;
    } else if (!EMAIL_REGEX.test(formData.email)) {
        newErrors.email = 'Please enter a valid email address';
        valid = false;
    }

    if (!formData.password) {
        newErrors.password = 'Password is required';
        valid = false;
    } else if (!PASSWORD_REGEX.test(formData.password)) {
        newErrors.password = 'Password must contain at least 8 characters, one uppercase, one lowercase, one number and one special character';
        valid = false;
    }

    if (!formData.passwordRetype) {
        newErrors.passwordMatch = 'Please confirm your password';
        valid = false;
    } else if (formData.password !== formData.passwordRetype) {
        newErrors.passwordMatch = 'Passwords do not match';
        valid = false;
    }

    if (!valid) {
        newErrors.form = 'Please fix all validation errors before submitting';
    }

    setErrors(newErrors);
    return valid;
};

const resetForm = () => {
    setFormData({
    fullName: '',
    email: '',
    countryCode: '+255',
    contact: '',
    title: '',
    nationality: '',
    affiliationId: '',
    affiliationName: '',
    typedAffiliation: '',
    password: '',
    passwordRetype: ''
    });
    setActiveStep(0);
    setAffiliationSearchInput('');
    setErrors({
        fullName: '',
        email: '',
        contact: '',
        title: '',
        nationality: '',
        affiliationId: '',
        password: '',
        passwordMatch: '',
        form: '',
        userExists: ''
    });
};

const handleNextStep = () => {
    if (validateCurrentStep()) {
        setActiveStep(prev => Math.min(prev + 1, 2));
        showToast("Step Complete", `Step ${activeStep + 1} completed successfully`, "success");
    } else {
        showToast("Validation Error", "Please complete all fields in the current step correctly", "warning");
    }
};

const handlePreviousStep = () => {
    setActiveStep(prev => Math.max(prev - 1, 0));
};

const handleGoBack = () => {
    navigate('/');
    showToast("Navigation", "Returning to home page", "info");
};

const handleLogIn = () => {
    navigate('/');
    showToast("Navigation", "Redirecting to login page", "info");
};

const handleSubmit = async () => {
    
    if (!validateForm()) {
        showToast("Validation Error", "Please fix all validation errors before submitting", "error");
        return;
    }

    setIsSubmitting(true);
    showToast("Processing", "Creating your account...", "info");

    try {
        const formDataToSend = new FormData();
        formDataToSend.append('fullName', formData.fullName);
        formDataToSend.append('email', formData.email);
        const fullContact = `${formData.countryCode}${formData.contact}`;
        formDataToSend.append('contact', fullContact);
        formDataToSend.append('university', formData.affiliationId); // Keep as 'university' for backend compatibility
        formDataToSend.append('title', formData.title);
        formDataToSend.append('password', formData.password);
        formDataToSend.append('nationality', formData.nationality);
        formDataToSend.append('countryCode', formData.countryCode);

        let postRequest;
        if (postStatus === "ADD") {
            postRequest = await axios.post(APIResearcherStore, formDataToSend, { headers });
        }

        const response = postRequest.data;
        if (response.status === "success") {
            resetForm();
            showToast("Success", response.message, "success");
            showToast("Email Alert","We have sent a verification link to your email. Please check your inbox.", "warning");
            setTimeout(() => navigate('/'), 5000);
        } else {
            showToast("Error", "Unknown Server Error, Account Not Created", "error");
        }
    } catch (error) {
        if (error.response) {
            if (error.response.status === 422) {
                const errors = error.response.data.errors;
                let errorMessages = [];
                for (const field in errors) {
                    errorMessages.push(...errors[field]);
                }
                showToast("Validation Error", errorMessages.join("\n"), "error");
            } else if (error.response.status === 409) {
                showToast("Error", "Email already registered. Please use a different email or login.", "error");
                setErrors(prev => ({ ...prev, email: 'This email is already registered' }));
            } else {
                showToast("Error", error.response.data.message || "An error occurred", "error");
            }
        } else if (error.code === "ERR_NETWORK") {
            showToast("Connection Error", TEXT_CONNECTION_ERROR, "error");
        } else {
            showToast("Error", "An unexpected error occurred", "error");
        }
    } finally {
        setIsSubmitting(false);
    }
};

const steps = ['Personal Info', 'Academic Details', 'Security'];

// Get selected country code display
const getSelectedCountryDisplay = () => {
    const selected = countryCodes.find(c => c.code === formData.countryCode);
    return selected ? `${selected.flag} ${selected.code}` : formData.countryCode;
};

if (loading) return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
    <CircularProgress />
    <Typography variant="h6" color="primary" sx={{ ml: 2 }}>
        Loading affiliations...
    </Typography>
    </Box>
);

return (
    <Container maxWidth="md" sx={{ py: 4 }}>
    <Zoom in={true}>
        <ModernCard elevation={0}>
        <CardContent sx={{ p: 4 }}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
            <LogoImage src={logoIcone} alt="RIA Logo" />
            <Typography 
                gutterBottom 
                sx={{ 
                fontSize: isMobile ? '1.8rem' : '2.125rem',
                background: 'linear-gradient(45deg, #1976d2, #00bcd4)',
                backgroundClip: 'text',
                textFillColor: 'transparent',
                fontWeight: 'bold',
                mt: 2
                }}
            >
                Join Research And Innovation Academy <br/> (RIA) Today!
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
                Create your account to access research opportunities and collaborations
            </Typography>
            </Box>

            <Stepper activeStep={activeStep} sx={{ mb: 4, overflowX: 'auto' }}>
            {steps.map((label) => (
                <Step key={label}>
                <StepLabel>{label}</StepLabel>
                </Step>
            ))}
            </Stepper>

            {error && (
            <Alert severity="error" sx={{ mb: 3 }} icon={<Cancel />}>
                <Typography variant="subtitle1">Error: {error}</Typography>
            </Alert>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Button 
                variant="outlined" 
                color="primary"
                onClick={() => setShowWhyModal(true)}
                startIcon={<Groups />}
                size={isMobile ? 'small' : 'medium'}
            >
                Why Join?
            </Button>
            
            <Box sx={{ display: 'flex', gap: 1 }}>
                {activeStep > 0 && (
                <Button 
                    variant="outlined"
                    onClick={handlePreviousStep}
                    startIcon={<ArrowBack />}
                >
                    Back
                </Button>
                )}
                {activeStep < 2 && (
                <Button 
                    variant="contained"
                    onClick={handleNextStep}
                    disabled={!stepValidations[activeStep]}
                >
                    Next
                </Button>
                )}
            </Box>
            </Box>

            <Box component="div">
            <Fade in={activeStep === 0} timeout={500}>
                <Box sx={{ display: activeStep === 0 ? 'block' : 'none' }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 3 }}>
                        <FormControl fullWidth error={!!errors.title}>
                            <InputLabel>Title *</InputLabel>
                            <Select
                                name="title"
                                value={formData.title}
                                onChange={handleInputChange}
                                label="Title *"
                            >
                                <MenuItem value="">Select Title</MenuItem>
                                <MenuItem value="Dr">Dr</MenuItem>
                                <MenuItem value="Mr">Mr</MenuItem>
                                <MenuItem value="Ms">Ms</MenuItem>
                                <MenuItem value="Mrs">Mrs</MenuItem>
                                <MenuItem value="Prof">Prof</MenuItem>
                                <MenuItem value="Other">Other</MenuItem>
                            </Select>
                            {errors.title && (
                                <FormHelperText>{errors.title}</FormHelperText>
                            )}
                        </FormControl>

                        <TextField
                            name="fullName"
                            label="Full Name *"
                            value={formData.fullName}
                            onChange={handleInputChange}
                            fullWidth
                            error={!!errors.fullName}
                            helperText={errors.fullName}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Person color="action" />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <Box>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button
                                    variant="outlined"
                                    onClick={() => setShowCountryCodeSelect(true)}
                                    sx={{ minWidth: '100px' }}
                                    startIcon={<Phone />}
                                >
                                    {getSelectedCountryDisplay()}
                                </Button>
                                <TextField
                                    name="contact"
                                    placeholder="Phone Number"
                                    value={formData.contact}
                                    onChange={handleInputChange}
                                    fullWidth
                                    error={!!errors.contact}
                                    helperText={errors.contact || `Enter ${formData.countryCode === '+1' ? '10' : '7-15'} digit number`}
                                    InputProps={{
                                        inputProps: {
                                            maxLength: formData.countryCode === '+1' ? 10 : 15,
                                            pattern: "[0-9]*",
                                            inputMode: "numeric"
                                        }
                                    }}
                                />
                            </Box>
                        </Box>

                        <TextField
                            name="nationality"
                            label="Nationality *"
                            value={formData.nationality}
                            onChange={handleInputChange}
                            fullWidth
                            error={!!errors.nationality}
                            helperText={errors.nationality}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Flag color="action" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Box>
                    
                    {/* Action Buttons for Step 1 */}
                    <Box sx={{ mt: 3 }}>
                        <Divider sx={{ my: 2 }} />
                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                            <ActionButton
                                variant="outlined"
                                color="secondary"
                                startIcon={<ArrowBack />}
                                onClick={handleGoBack}
                                sx={{
                                    borderColor: '#FF6B6B',
                                    color: '#FF6B6B',
                                    borderRadius: 20,
                                    '&:hover': {
                                        borderColor: '#FF6B6B',
                                        backgroundColor: 'rgba(255, 107, 107, 0.1)',
                                    }
                                }}
                            >
                                Back to Home
                            </ActionButton>
                            <ActionButton
                                variant="contained"
                                startIcon={<Login />}
                                onClick={handleLogIn}
                                sx={{
                                    borderRadius: 20,
                                    background: 'linear-gradient(135deg, #10B981, #059669)',
                                    '&:hover': {
                                        background: 'linear-gradient(135deg, #059669, #047857)',
                                    }
                                }}
                            >
                                Sign In to Existing Account
                            </ActionButton>
                        </Box>
                    </Box>
                </Box>
            </Fade>

            <Fade in={activeStep === 1} timeout={500}>
                <Box sx={{ display: activeStep === 1 ? 'block' : 'none' }}>
                    <Box sx={{ mb: 3 }}>
                        <Autocomplete
                            id="affiliation-select"
                            options={affiliations}
                            loading={loading}
                            value={affiliations.find(a => a.id === formData.affiliationId) || null}
                            onChange={handleAffiliationSelect}
                            inputValue={affiliationSearchInput}
                            onInputChange={(event, newInputValue) => {
                                setAffiliationSearchInput(newInputValue);
                            }}
                            getOptionLabel={(option) => {
                                if (typeof option === 'string') return option;
                                return option.name || '';
                            }}
                            isOptionEqualToValue={(option, value) => option.id === value?.id}
                            renderOption={(props, option) => (
                                <li {...props}>
                                    <Box>
                                        <Typography variant="body1">
                                            <strong>{option.name}</strong>
                                        </Typography>
                                        {option.code && (
                                            <Typography variant="caption" color="text.secondary">
                                                Code: {option.code}
                                            </Typography>
                                        )}
                                    </Box>
                                </li>
                            )}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Select Affiliation / Institution *"
                                    required
                                    error={!!errors.affiliationId}
                                    helperText={errors.affiliationId || "Search and select your affiliation"}
                                    InputProps={{
                                        ...params.InputProps,
                                        startAdornment: (
                                            <>
                                                <InputAdornment position="start">
                                                    <School color="action" />
                                                </InputAdornment>
                                                {params.InputProps.startAdornment}
                                            </>
                                        ),
                                        endAdornment: (
                                            <>
                                                {loading && <CircularProgress color="inherit" size={20} />}
                                                <Badge 
                                                    badgeContent={affiliations.length} 
                                                    color="primary"
                                                    sx={{ mr: 1 }}
                                                />
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />
                        
                        {/* Add New Affiliation Button */}
                        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                            <Button
                                variant="outlined"
                                color="secondary"
                                startIcon={<Add />}
                                onClick={() => setShowAddAffiliationDialog(true)}
                                sx={{
                                    borderRadius: 20,
                                    textTransform: 'none'
                                }}
                            >
                                My Affiliation Not Listed? Add New Affiliation
                            </Button>
                        </Box>
                    </Box>

                    <TextField
                        name="email"
                        label="Email *"
                        value={formData.email}
                        onChange={handleInputChange}
                        fullWidth
                        error={!!errors.email}
                        helperText={errors.email}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Email color="action" />
                                </InputAdornment>
                            ),
                        }}
                    />
                    
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2.5,
                            mb: 3,
                            borderRadius: 2,
                            marginTop: 3,
                            background: 'linear-gradient(135deg, #f0f7ff 0%, #e3f2fd 100%)',
                            border: '1px solid',
                            borderColor: 'primary.100',
                            position: 'relative',
                            overflow: 'hidden',
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: 4,
                                height: '100%',
                                backgroundColor: 'primary.main',
                            }
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                            <Email color="primary" sx={{ mt: 0.5 }} />
                            <Box>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: 'primary.dark', mb: 1 }}>
                                    Verify Your Email to Get Started
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'error.main', lineHeight: 1.6 }}>
                                    Check your inbox immediately after registration for a verification link. 
                                    Your account will be activated once you click the link in the email.
                                </Typography>
                            </Box>
                        </Box>
                    </Paper>
                </Box>
            </Fade>

            <Fade in={activeStep === 2} timeout={500}>
                <Box sx={{ display: activeStep === 2 ? 'grid' : 'none', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 3 }}>
                    <Box>
                        <TextField
                            name="password"
                            label="Password *"
                            type={showPassword ? 'text' : 'password'}
                            value={formData.password}
                            onChange={handleInputChange}
                            fullWidth
                            error={!!errors.password}
                            helperText={errors.password}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Lock color="action" />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword(!showPassword)}
                                            edge="end"
                                            aria-label={showPassword ? "Hide password" : "Show password"}
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                        {formData.password && (
                            <>
                                <PasswordStrengthBar strength={passwordStrength} />
                                <Typography variant="caption" color="text.secondary">
                                    Password strength: {passwordStrength}
                                </Typography>
                            </>
                        )}
                    </Box>

                    <TextField
                        name="passwordRetype"
                        label="Confirm Password *"
                        type={showPassword ? 'text' : 'password'}
                        value={formData.passwordRetype}
                        onChange={handleInputChange}
                        fullWidth
                        error={!!errors.passwordMatch}
                        helperText={errors.passwordMatch}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Lock color="action" />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>
            </Fade>

            {errors.form && (
                <Box sx={{ mt: 2 }}>
                    <Alert severity="error">{errors.form}</Alert>
                </Box>
            )}

            {activeStep === 2 && (
                <Slide direction="up" in={activeStep === 2} timeout={500}>
                    <Box sx={{ mt: 3 }}>
                        <Button
                            variant="contained"
                            color="primary"
                            size="large"
                            disabled={isSubmitting || !stepValidations[2]}
                            fullWidth
                            onClick={handleSubmit}
                            startIcon={isSubmitting ? <CircularProgress size={20} /> : <Send />}
                            sx={{ 
                                py: 1.5, 
                                fontSize: '1.1rem',
                                position: 'relative',
                                overflow: 'hidden',
                            }}
                        >
                            {isSubmitting ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CircularProgress size={20} color="inherit" />
                                    Creating Your Account...
                                </Box>
                            ) : (
                                'Create Account'
                            )}
                        </Button>

                        {isSubmitting && (
                            <Box sx={{ width: '100%', mt: 1 }}>
                                <LinearProgress
                                    sx={{ 
                                        height: 4,
                                        borderRadius: 2,
                                        '& .MuiLinearProgress-bar': {
                                            animation: 'pulse 1.5s ease-in-out infinite'
                                        }
                                    }}
                                />
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
                                    Setting up your research account...
                                </Typography>
                            </Box>
                        )}

                        <Button
                            variant="outlined"
                            color="secondary"
                            size="large"
                            fullWidth
                            sx={{ mt: 2 }}
                            onClick={() => navigate('/')}
                            startIcon={<Login />}
                            disabled={isSubmitting}
                        >
                            Already have an account? Sign In
                        </Button>
                    </Box>
                </Slide>
            )}
            </Box>
        </CardContent>
        </ModernCard>
    </Zoom>

    {/* Why Register Modal */}
    <StyledModal open={showWhyModal} onClose={() => setShowWhyModal(false)}>
        <ModalContent>
        <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Groups color="primary" />
            Why Join RIA?
        </Typography>
        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FeatureChip icon={<CheckCircle />} label="Access to all research features" />
            <FeatureChip icon={<CheckCircle />} label="Submit your research projects" />
            <FeatureChip icon={<CheckCircle />} label="Track your submissions in real-time" />
            <FeatureChip icon={<CheckCircle />} label="Receive important notifications" />
            <FeatureChip icon={<CheckCircle />} label="Collaborate with researchers" />
            <FeatureChip icon={<CheckCircle />} label="Access funding opportunities" />
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Button 
            variant="contained" 
            onClick={() => setShowWhyModal(false)}
            startIcon={<DoneAll />}
            >
            Got It
            </Button>
        </Box>
        </ModalContent>
    </StyledModal>

    {/* Country Code Selection Modal */}
    <StyledModal open={showCountryCodeSelect} onClose={() => setShowCountryCodeSelect(false)}>
        <ModalContent>
        <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Phone color="primary" />
            Select Country Code
        </Typography>
        <Divider sx={{ my: 2 }} />
        
        <TextField
            placeholder="Search country..."
            onChange={(e) => {
                const searchTerm = e.target.value.toLowerCase();
                // Filter logic would go here
            }}
            fullWidth
            sx={{ mb: 2 }}
            InputProps={{
                startAdornment: (
                    <InputAdornment position="start">
                        <Search color="action" />
                    </InputAdornment>
                ),
            }}
        />

        <UniversityList>
            {countryCodes.map((country, index) => (
                <ListItem
                    key={country.code}
                    button
                    onClick={() => handleCountryCodeChange(country.code)}
                    selected={formData.countryCode === country.code}
                >
                    <ListItemText 
                        primary={`${country.flag} ${country.code} - ${country.country}`}
                        secondary={`Example: ${country.example}`}
                        primaryTypographyProps={{ fontSize: isMobile ? '0.875rem' : '1rem' }}
                    />
                </ListItem>
            ))}
        </UniversityList>

        <Box sx={{ 
            display: 'flex', 
            justifyContent: 'flex-end',
            mt: 3,
        }}>
            <Button
                variant="contained"
                onClick={() => setShowCountryCodeSelect(false)}
                startIcon={<DoneAll />}
                fullWidth={isMobile}
            >
                Close
            </Button>
        </Box>
        </ModalContent>
    </StyledModal>

    {/* Add New Affiliation Dialog */}
    <Dialog 
        open={showAddAffiliationDialog} 
        onClose={() => !isAddingAffiliation && setShowAddAffiliationDialog(false)}
        maxWidth="sm"
        fullWidth
    >
        <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" component="div" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <School color="primary" />
                    Add New Affiliation
                </Typography>
                <IconButton
                    edge="end"
                    color="inherit"
                    onClick={() => setShowAddAffiliationDialog(false)}
                    disabled={isAddingAffiliation}
                >
                    <Close />
                </IconButton>
            </Box>
        </DialogTitle>
        <DialogContent>
            <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Please enter your affiliation details. Both fields are required.
                </Typography>
                <TextField
                    autoFocus
                    label="Affiliation Name *"
                    fullWidth
                    value={newAffiliationName}
                    onChange={(e) => setNewAffiliationName(e.target.value)}
                    disabled={isAddingAffiliation}
                    placeholder="e.g., University of Dar es Salaam, Research Institute, etc."
                    helperText="Enter the full official name of your institution/organization"
                    sx={{ mb: 2 }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <School color="action" />
                            </InputAdornment>
                        ),
                    }}
                />
                <TextField
                    label="Creator Name *"
                    fullWidth
                    value={newAffiliationCreator}
                    onChange={(e) => setNewAffiliationCreator(e.target.value)}
                    disabled={isAddingAffiliation}
                    placeholder="Your full name"
                    helperText="Enter your name as the creator of this affiliation"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Person color="action" />
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
            <Button 
                onClick={() => setShowAddAffiliationDialog(false)} 
                disabled={isAddingAffiliation}
                startIcon={<Cancel />}
            >
                Cancel
            </Button>
            <Button 
                onClick={handleAddNewAffiliation} 
                variant="contained" 
                color="primary"
                disabled={isAddingAffiliation || !newAffiliationName.trim() || !newAffiliationCreator.trim()}
                startIcon={isAddingAffiliation ? <CircularProgress size={20} /> : <Add />}
            >
                {isAddingAffiliation ? 'Adding...' : 'Add Affiliation'}
            </Button>
        </DialogActions>
    </Dialog>

    {/* Affiliation Selection Modal - Keeping for backward compatibility but not used */}
    <StyledModal open={showAffiliationModal} onClose={() => setShowAffiliationModal(false)}>
        <ModalContent>
        <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <School color="primary" />
            Select Your Affiliation
            <Badge 
            badgeContent={affiliations.length} 
            color="primary"
            sx={{ ml: 1 }}
            />
        </Typography>
        
        <Button
            variant="outlined"
            color="secondary"
            onClick={() => setShowAffiliationNotListed(!showAffiliationNotListed)}
            sx={{ mb: 2 }}
            startIcon={showAffiliationNotListed ? <ArrowBack /> : <School />}
        >
            {showAffiliationNotListed ? 'Back to List' : 'My Affiliation Not Listed'}
        </Button>

        {showAffiliationNotListed ? (
            <TextField
            label="Type Affiliation Name"
            value={formData.typedAffiliation}
            onChange={(e) => {
                setFormData(prev => ({
                ...prev,
                typedAffiliation: e.target.value,
                affiliationName: e.target.value,
                affiliationId: '0000'
                }));
                setErrors(prev => ({ ...prev, affiliationId: '' }));
            }}
            fullWidth
            sx={{ mb: 2 }}
            InputProps={{
                startAdornment: (
                <InputAdornment position="start">
                    <School color="action" />
                </InputAdornment>
                ),
            }}
            />
        ) : (
            <>
            <TextField
                label="Search Affiliations"
                value={affiliationSearchInput}
                onChange={(e) => setAffiliationSearchInput(e.target.value)}
                fullWidth
                sx={{ mb: 2 }}
                InputProps={{
                startAdornment: (
                    <InputAdornment position="start">
                    <Search color="action" />
                    </InputAdornment>
                ),
                }}
            />

            <UniversityList>
                {affiliations
                    .filter(aff => aff.name.toLowerCase().includes(affiliationSearchInput.toLowerCase()))
                    .map((affiliation, index) => (
                    <ListItem
                        key={affiliation.id}
                        button
                        onClick={() => {
                            handleAffiliationSelect(null, affiliation);
                            setShowAffiliationModal(false);
                        }}
                        selected={formData.affiliationId === affiliation.id}
                    >
                        <ListItemText 
                            primary={`${index + 1}. ${affiliation.name}`}
                            primaryTypographyProps={{ fontSize: isMobile ? '0.875rem' : '1rem' }}
                        />
                    </ListItem>
                ))}
            </UniversityList>
            </>
        )}

        <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            mt: 3,
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 2 : 0
        }}>
            {formData.affiliationName && (
            <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircle color="success" />
                Selected: <strong>{formData.affiliationName}</strong>
            </Typography>
            )}

            <Button
            variant="contained"
            onClick={() => setShowAffiliationModal(false)}
            startIcon={<DoneAll />}
            fullWidth={isMobile}
            >
            Confirm Selection
            </Button>
        </Box>
        </ModalContent>
    </StyledModal>
    </Container>
);
};

export default Register;